import React, { useRef, useState, useEffect } from "react";
import { Button, ButtonVariants, useAuth, UserLogin } from "@app/shared";
import { useModel } from "../../../../../hooks";
import { NavLink } from "react-router-dom";
import { Pages } from "../../..";
import { getWizardState, updateUserEmail } from "../../utils/wizardStorage";
import { LoginFormFields } from "../../../../shared/LoginFormFields";

interface LoginWizardProps {
    onSuccess: () => void;
    onCreateAccountClick: () => void;
    onForgotPasswordClick: () => void;
}

export const LoginWizard: React.FC<LoginWizardProps> = ({ onSuccess, onCreateAccountClick, onForgotPasswordClick }) => {
    const [credentials, { bindingsFor, validateWithAction, modelContainerClassNames, ErrorSummary }] = useModel(UserLogin);
    const submitButton = useRef<HTMLButtonElement>(null);
    const { logIn } = useAuth();
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const savedState = getWizardState();
        if (savedState.userEmail && !credentials.email) {
            credentials.email = savedState.userEmail;
        }
    }, []);

    const handleEmailChange = (email: string) => {
        if (email) {
            updateUserEmail(email);
        }
    };

    async function handleLogInOnClick() {
        setIsLoading(true);
        try {
            await validateWithAction(async () => {
                if (credentials.email) {
                    updateUserEmail(credentials.email);
                }
                await logIn(credentials as UserLogin);
                onSuccess();
            });
        } catch (error) {
            console.error("Login error:", error);
        } finally {
            setIsLoading(false);
        }
    }

    const handleForgotPasswordClick = () => {
        if (credentials.email) {
            updateUserEmail(credentials.email);
        }
        onForgotPasswordClick();
    };

    const emailBindingsWithPersistence = {
        ...bindingsFor("email"),
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
            bindingsFor("email").onChange(e);
            handleEmailChange(e.target.value);
        },
    };

    return (
        <div className="w-full">
            <div className="mb-6 text-center">
                <h2 className="text-2xl font-bold mb-2">Sign In to Your Account</h2>
                <p>Access your device dashboard</p>
            </div>

            <div className="bg-white rounded-lg shadow-sm border p-6">
                <div className={`${modelContainerClassNames} space-y-4`}>
                    <LoginFormFields emailBindings={emailBindingsWithPersistence} passwordBindings={bindingsFor("password")} ErrorSummary={ErrorSummary} onPasswordEnter={() => submitButton.current?.click()} showErrorSummary={true} />
                </div>

                <div className="mt-6 space-y-3">
                    <Button ref={submitButton} onClick={handleLogInOnClick} className="w-full" disabled={isLoading}>
                        {isLoading ? "Signing In..." : "Sign In"}
                    </Button>

                    <div className="flex flex-col content-center justify-center w-100 space-y-2">
                        <Button className="w-full" variant={ButtonVariants.Secondary} onClick={handleForgotPasswordClick}>
                            Forgot your password?
                        </Button>
                        <Button className="w-full" variant={ButtonVariants.Secondary} onClick={onCreateAccountClick}>
                            Create a new account
                        </Button>
                        <NavLink className="text-center text-sm rounded-xl transition-all duration-200 font-medium shadow-md hover:shadow-lg transform hover:-translate-y-0.5 py-2" to={Pages.Terms.getUrl()}>
                            Terms and Conditions
                        </NavLink>
                    </div>
                </div>
            </div>
        </div>
    );
};
